import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import { DashboardCard, SectionTitle } from '../../../../../ui'

const RESULT_CODES = new Set(['3-1-0-0', '3-2-0-0'])

const formatPeriodLabel = (period, showYear) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(period || ''))
  if (!match) return String(period || '').toUpperCase()

  const [, year, month] = match
  const monthLabel = new Intl.DateTimeFormat('es-CL', {
    month: 'long'
  }).format(new Date(Number(year), Number(month) - 1, 1))

  return `${monthLabel}${showYear ? ` ${year}` : ''}`.toUpperCase()
}

const formatAmount = value => {
  const amount = Number(value ?? 0)
  const formattedValue = new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0
  }).format(Math.abs(Number.isFinite(amount) ? amount : 0))

  return amount < 0 ? `(${formattedValue})` : formattedValue
}

const getAmountToneClass = value =>
  Number(value ?? 0) === 0 ? 'font-normal text-slate-400' : 'text-slate-700'

const getPeriods = (summary, rows) => {
  if (Array.isArray(summary?.periods) && summary.periods.length) {
    return summary.periods
  }

  return Array.from(new Set(rows.flatMap(row => Object.keys(row?.monthlyData || {})))).sort()
}

const EstadoResultadoResumenTable = ({ summary, loading = false }) => {
  const rows = Array.isArray(summary?.rows) ? summary.rows : []
  const currentYear = new Date().getFullYear()
  const periods = getPeriods(summary, rows).filter(period =>
    String(period).startsWith(`${currentYear}-`)
  )
  const showYear = false

  if (!loading && !rows.length) return null

  return (
    <DashboardCard className='w-full min-w-0 overflow-hidden p-3 sm:p-6'>
      <SectionTitle className='mb-1 text-center text-base sm:mb-4 sm:text-lg'>
        Resumen Estado de Resultados {currentYear}
      </SectionTitle>
      <p className='mb-3 text-center text-[10px] text-slate-400 sm:hidden'>
        Desliza horizontalmente para revisar todos los períodos
      </p>

      {loading && !rows.length ? (
        <div className='flex h-32 items-center justify-center' role='status'>
          <Lottie
            animationData={LoaderMain}
            loop
            autoplay
            style={{ width: 100, height: 100 }}
          />
          <span className='sr-only'>Cargando resumen de estado de resultados</span>
        </div>
      ) : (
        <div className='relative w-full max-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white font-sans text-[9px] leading-4 text-slate-700 sm:text-[11px] sm:leading-5'>
          <table
            className='w-full table-fixed border-separate border-spacing-0 text-[9px] sm:text-[11px]'
            style={{ minWidth: `${132 + periods.length * 88 + 104}px` }}
          >
            <caption className='sr-only'>Resumen mensual del estado de resultados</caption>
            <colgroup>
              <col className='w-[132px] sm:w-[220px] lg:w-[240px]' />
              {periods.map(period => (
                <col key={`column-${period}`} className='w-[88px] sm:w-[100px] lg:w-[104px]' />
              ))}
              <col className='w-[104px] sm:w-[112px] lg:w-[120px]' />
            </colgroup>
            <thead>
              <tr>
                <th className='sticky left-0 top-0 z-30 whitespace-normal border-b border-r border-slate-200 bg-white px-2 py-2 text-left text-[8px] font-bold uppercase tracking-wide text-slate-500 sm:px-4 sm:py-3 sm:text-[10px]'>
                  Cuenta
                </th>
                {periods.map(period => (
                  <th
                    key={period}
                    className='sticky top-0 z-20 whitespace-nowrap border-b border-slate-200 bg-white px-2 py-2 text-right text-[8px] font-bold uppercase tracking-wide text-slate-500 sm:px-3 sm:py-3 sm:text-[10px]'
                  >
                    {formatPeriodLabel(period, showYear)}
                  </th>
                ))}
                <th className='sticky top-0 z-20 whitespace-nowrap border-b border-l border-slate-200 bg-white px-2 py-2 text-right text-[8px] font-semibold uppercase tracking-wide text-slate-700 sm:px-3 sm:py-3 sm:text-[10px]'>
                  Acumulado
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const code = String(row?.codeCuenta || '')
                const isResult = RESULT_CODES.has(code)
                const isTotal = code === 'TOTAL'
                const currentYearAccumulated = periods.reduce(
                  (total, period) => total + Number(row?.monthlyData?.[period] ?? 0),
                  0
                )
                const accountLabel = isTotal
                  ? 'RESULTADO'
                  : isResult
                    ? row?.nameCuenta || code
                    : `${code} ${row?.nameCuenta || ''}`.trim()

                return (
                  <tr
                    key={`${code}-${index}`}
                    className={clsx(
                      'group',
                      isResult || isTotal
                        ? 'bg-slate-50 hover:bg-slate-100/70'
                        : 'bg-white hover:bg-slate-50'
                    )}
                  >
                    <td
                      className={clsx(
                        'sticky left-0 z-10 whitespace-normal break-words border-b border-r px-2 text-left text-[9px] leading-4 group-hover:bg-slate-50 sm:px-4 sm:text-[11px] sm:leading-5',
                        isResult &&
                          'border-t border-slate-200 bg-slate-50 py-3 font-bold text-slate-800 group-hover:bg-slate-100/70',
                        isTotal &&
                          'border-t border-slate-300 bg-slate-50 py-3 font-bold text-slate-900 group-hover:bg-slate-100/70',
                        !isResult &&
                          !isTotal &&
                          'border-slate-100 bg-white py-2.5 pl-8 font-bold text-slate-800'
                      )}
                    >
                      {accountLabel}
                    </td>

                    {periods.map(period => {
                      const value = row?.monthlyData?.[period]

                      return (
                        <td
                          key={`${code}-${period}`}
                          className={clsx(
                            'whitespace-nowrap px-2 text-right text-[9px] font-bold tabular-nums sm:px-3 sm:text-[11px]',
                            isResult
                              ? 'border-b border-t border-slate-200 py-3'
                              : isTotal
                                ? 'border-b border-t border-slate-300 py-3'
                                : 'border-b border-slate-100 py-2.5',
                            getAmountToneClass(value)
                          )}
                        >
                          {formatAmount(value)}
                        </td>
                      )
                    })}

                    <td
                      className={clsx(
                        'whitespace-nowrap border-l border-slate-200 px-2 text-right text-[9px] font-semibold tabular-nums sm:px-3 sm:text-[11px]',
                        isResult
                          ? 'border-b border-t py-3'
                          : isTotal
                            ? 'border-b border-t border-t-slate-300 py-3'
                            : 'border-b py-2.5',
                        getAmountToneClass(currentYearAccumulated)
                      )}
                    >
                      {formatAmount(currentYearAccumulated)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  )
}

export default EstadoResultadoResumenTable
